import {
	Box,
	Avatar,
	Button,
	TextField,
	Select,
	Chip,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// ──────────────────────────────────────────────
// Shared page container
// ──────────────────────────────────────────────

export const PageContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(4),
	backgroundColor: theme.palette.background.default,
	minHeight: 'calc(100vh - 120px)',
}));

// ──────────────────────────────────────────────
// Hub (Level 0) — materia cards grid
// ──────────────────────────────────────────────

export const ChangesNotice = styled(Typography)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	color: '#E65100',
	fontWeight: 600,
}));

export const HubGrid = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
	gap: theme.spacing(3),
	[theme.breakpoints.down('sm')]: {
		gridTemplateColumns: '1fr',
	},
}));

export const HubEmptyState = styled(Box)(({ theme }) => ({
	textAlign: 'center',
	marginTop: theme.spacing(10),
	padding: theme.spacing(4),
}));

// ──────────────────────────────────────────────
// Detail (Level 1) — resource list
// ──────────────────────────────────────────────

export const DetailHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: theme.spacing(3),
	padding: theme.spacing(2.5, 3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: theme.spacing(2),
	},
}));

export const DetailHeaderLeft = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}));

export const DetailHeaderRight = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
	flexShrink: 0,
}));

export const CodigoChip = styled(Chip)(({ theme }) => ({
	fontFamily: 'monospace',
	fontWeight: 700,
	fontSize: '0.8rem',
	letterSpacing: '0.1em',
	cursor: 'pointer',
	backgroundColor: theme.palette.primary.main + '14',
	color: theme.palette.primary.main,
	border: `1px solid ${theme.palette.primary.main}40`,
	'&:hover': {
		backgroundColor: theme.palette.primary.main + '22',
	},
}));

// ──────────────────────────────────────────────
// Filters
// ──────────────────────────────────────────────

export const SingleFilterContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	gap: theme.spacing(2),
	marginBottom: theme.spacing(3),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}));

export const FiltersContainer = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr auto',
	gap: theme.spacing(2),
	marginBottom: theme.spacing(3),
	padding: theme.spacing(3),
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	alignItems: 'center',
	[theme.breakpoints.down('sm')]: {
		gridTemplateColumns: '1fr',
	},
}));

export const FilterSelect = styled(Select)(({ theme }) => ({
	minWidth: 200,
	backgroundColor: theme.palette.background.default,
	[theme.breakpoints.down('sm')]: {
		minWidth: '100%',
	},
}));

export const SearchField = styled(TextField)(({ theme }) => ({
	'& .MuiOutlinedInput-root': {
		backgroundColor: theme.palette.background.default,
	},
}));

export const AppliedFiltersContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	gap: theme.spacing(1),
	alignItems: 'center',
	marginBottom: theme.spacing(3),
}));

export const FilterChip = styled(Chip)(({ theme }) => ({
	backgroundColor: theme.palette.primary.main,
	color: theme.palette.primary.contrastText,
	fontWeight: 500,
	'& .MuiChip-deleteIcon': {
		color: theme.palette.primary.contrastText,
	},
}));

export const ClearFiltersButton = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(0.5),
	cursor: 'pointer',
	color: theme.palette.text.secondary,
	fontSize: '0.875rem',
	marginLeft: 'auto',
	'&:hover': {
		color: theme.palette.primary.main,
	},
}));

// ──────────────────────────────────────────────
// Results header + upload button
// ──────────────────────────────────────────────

export const ResultsHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: theme.spacing(3),
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: theme.spacing(2),
	},
}));

export const ReleaseButton = styled(Button)(({ theme }) => ({
	fontWeight: 600,
	borderRadius: theme.shape.borderRadius,
	textTransform: 'none',
}));

export const UploadButton = styled(Button)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	paddingLeft: theme.spacing(2.5),
	paddingRight: theme.spacing(2.5),
	fontWeight: 600,
	borderRadius: theme.shape.borderRadius,
	textTransform: 'none',
	boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
	'&:hover': {
		boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
	},
}));

// ──────────────────────────────────────────────
// Table
// ──────────────────────────────────────────────

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
}));

export const StyledTable = styled(Table)(() => ({
	minWidth: 700,
}));

export const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
	padding: theme.spacing(1.5, 2),
	fontWeight: 600,
	backgroundColor: theme.palette.grey[50],
	borderBottom: `2px solid ${theme.palette.divider}`,
	color: theme.palette.text.secondary,
	fontSize: '0.8rem',
	textTransform: 'uppercase',
	letterSpacing: '0.05em',
	whiteSpace: 'nowrap',
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	padding: theme.spacing(1.75, 2),
	borderBottom: `1px solid ${theme.palette.divider}`,
	verticalAlign: 'middle',
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:hover': {
		backgroundColor: theme.palette.action.hover,
	},
	'&:last-child td': {
		borderBottom: 0,
	},
}));

export const FileNameCell = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.5),
}));

const getFileIconColors = (resourcetype: string, theme: any) => {
	switch (resourcetype) {
		case 'pdf':
			return { bg: '#FDECEA', color: '#C62828' };
		case 'docx':
			return { bg: '#E3F2FD', color: '#1565C0' };
		case 'pptx':
			return { bg: '#FFF3E0', color: '#E65100' };
		case 'xlsx':
			return { bg: '#E8F5E9', color: '#2E7D32' };
		case 'video':
			return { bg: '#F3E5F5', color: '#6A1B9A' };
		case 'imagen':
			return { bg: '#E0F7FA', color: '#00697A' };
		case 'link':
			return { bg: '#EDE7F6', color: '#4527A0' };
		default:
			return { bg: theme.palette.grey[100], color: theme.palette.grey[600] };
	}
};

export const FileIconContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'resourcetype',
})<{ resourcetype: string }>(({ theme, resourcetype }) => {
	const { bg, color } = getFileIconColors(resourcetype, theme);
	return {
		width: 36,
		height: 36,
		borderRadius: theme.shape.borderRadius,
		backgroundColor: bg,
		color,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		fontSize: '0.65rem',
		fontWeight: 700,
		letterSpacing: '0.03em',
	};
});

export const ReviewerCell = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: 2,
	minWidth: 0,
});

export const ReviewerEmail = styled('a')(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: '0.75rem',
	lineHeight: 1.3,
	textDecoration: 'none',
	wordBreak: 'break-all',
	'&:hover': {
		textDecoration: 'underline',
	},
}));

export const StatusBadge = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'status',
})<{ status: string }>(({ theme, status }) => {
	const getBg = () => {
		switch (status) {
			case 'aprobado':
				return theme.palette.success.main;
			case 'en_revision':
				return theme.palette.info.main;
			case 'cambios_solicitados':
				return '#FBC02D';
			case 'denegado':
				return theme.palette.error.main;
			default:
				return theme.palette.grey[400];
		}
	};
	return {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 160,
		padding: '4px 10px',
		borderRadius: 12,
		fontSize: '0.75rem',
		fontWeight: 600,
		backgroundColor: getBg(),
		color: status === 'cambios_solicitados' ? '#333333' : '#fff',
		whiteSpace: 'nowrap',
	};
});

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
	textAlign: 'center',
	marginTop: theme.spacing(8),
	padding: theme.spacing(4),
}));

// ──────────────────────────────────────────────
// Tabs (detail view)
// ──────────────────────────────────────────────

export const TabsContainer = styled(Box)(({ theme }) => ({
	borderBottom: `1px solid ${theme.palette.divider}`,
	marginBottom: theme.spacing(3),
}));

// ──────────────────────────────────────────────
// Alumno list
// ──────────────────────────────────────────────

export const AlumnoListContainer = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	borderRadius: theme.shape.borderRadius,
	boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	overflow: 'hidden',
}));

export const AlumnoRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(2),
	padding: theme.spacing(1.75, 2.5),
	borderBottom: `1px solid ${theme.palette.divider}`,
	'&:last-child': {
		borderBottom: 0,
	},
	'&:hover': {
		backgroundColor: theme.palette.action.hover,
	},
}));

export const AlumnoAvatar = styled(Avatar)(({ theme }) => ({
	width: 36,
	height: 36,
	fontSize: '0.8rem',
	fontWeight: 700,
	backgroundColor: theme.palette.primary.main + '22',
	color: theme.palette.primary.main,
}));

export const AlumnoInfo = styled(Box)(() => ({
	flex: 1,
	minWidth: 0,
}));

export const AlumnoListHeader = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	padding: theme.spacing(1.5, 2.5),
	backgroundColor: theme.palette.grey[50],
	borderBottom: `2px solid ${theme.palette.divider}`,
}));
